/**
 * Free-element geometry: the rotation family. Every affordance on a free
 * element (2D plan, 3D box, hit-testing, side/corner drags) is authored in
 * the element's local frame and projected through these - rotation is never
 * a special case (see the decision log).
 */
import { EdgeName, FloorPlanFreeElement, freeElementRotation, Point } from '../schemas';
import { rectRing } from './rings';
import { degreesToRadians, rotateAbout, rotateVector } from './rotation';

/** A free element's plan rotation in radians (0 when unset). */
export const freeElementRadians = (element: FloorPlanFreeElement): number => degreesToRadians(freeElementRotation(element));

/**
 * Room-space rotation about the element's center, cosine/sine computed once.
 * `inverse` rotates the other way - world → the element's local (unrotated) frame.
 */
export function freeElementRotator(element: FloorPlanFreeElement, inverse = false): (point: Point) => Point {
  const radians = inverse ? -freeElementRadians(element) : freeElementRadians(element);
  if (!radians) return point => point;
  const cosine = Math.cos(radians), sine = Math.sin(radians);
  const center = { x: element.x + element.width / 2, y: element.y + element.length / 2 };
  return point => rotateAbout(point, center, cosine, sine);
}

/** Rotate a direction vector by the element's plan rotation (no translation). */
export function freeElementRotateVector(element: FloorPlanFreeElement, vector: Point): Point {
  return rotateVector(vector, freeElementRadians(element));
}

/**
 * A free element's footprint ring in room coordinates - the chamfered rect,
 * rotated about its center when the element is a rotated object. The single
 * source for the 2D plan, the 3D box, and hit-testing.
 */
export function freeElementRing(element: FloorPlanFreeElement): Point[] {
  return rectRing(element.x, element.y, element.width, element.length, element).map(freeElementRotator(element));
}

/**
 * The four rectangle corners in room space, rotation applied, chamfers
 * ignored: [backLeft, backRight, frontRight, frontLeft].
 */
export function freeElementRectangleCorners(element: FloorPlanFreeElement): [Point, Point, Point, Point] {
  const rotate = freeElementRotator(element);
  const { x, y, width, length } = element;
  return [
    rotate({ x, y }),
    rotate({ x: x + width, y }),
    rotate({ x: x + width, y: y + length }),
    rotate({ x, y: y + length }),
  ];
}

/** One side of an element's footprint, rotation applied. */
export interface ElementSide {
  side: EdgeName;
  a: Point;
  b: Point;
}

/** The element's four sides in boundary-walk order: back, right, front, left. */
export function freeElementSides(element: FloorPlanFreeElement): ElementSide[] {
  const [backLeft, backRight, frontRight, frontLeft] = freeElementRectangleCorners(element);
  return [
    { side: 'backEdge', a: backLeft, b: backRight },
    { side: 'rightEdge', a: backRight, b: frontRight },
    { side: 'frontEdge', a: frontRight, b: frontLeft },
    { side: 'leftEdge', a: frontLeft, b: backLeft },
  ];
}

/** A side's outward unit normal in room space, rotation applied. */
export function freeElementSideNormal(element: FloorPlanFreeElement, side: EdgeName): Point {
  return freeElementRotateVector(
    element,
    side === 'backEdge'
      ? { x: 0, y: -1 }
      : side === 'frontEdge'
      ? { x: 0, y: 1 }
      : side === 'leftEdge'
      ? { x: -1, y: 0 }
      : { x: 1, y: 0 },
  );
}
